/**
 * The account's tags, and the one description of how a field that takes one behaves.
 *
 * Two things live here rather than one, and they belong together: the shape
 * `GET /api/tags` answers, and the rules every `#tag` field obeys — because the second
 * is what the first exists to serve. A tag list with counts is only worth a route
 * because a menu orders by them.
 *
 * In `:clientServer` for [ApiRoutes]' reason. This is the one module compiled into both
 * the browser bundle and the server, so it is the only place a rule can be written where
 * *both* implementations of it can point at the same sentence and have the link resolve.
 *
 * @see ApiRoutes.TAGS
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * One tag the account uses, and what is known about it.
 *
 * @property tag the tag itself, in the shape it is stored — trimmed, lower-cased and
 *   starting with `#`. See [Bookmark.tags], which carries the argument for both rules;
 *   the short version is that `#Rust` and `rust` are one label rather than three.
 * @property uses how many bookmarks carry it.
 *
 *   **Shown, not just sorted by.** `#automation` at 25 and `#automotive` at 3 look
 *   alike at a glance, and the number is what stops the near-duplicate being picked by
 *   accident — which is the single failure mode a shared tag control exists to prevent.
 *   It is the same reason *Create …* is drawn last; see [TagInputRules].
 * @property fromAi whether **no** bookmark carrying this tag was ever tagged with it by
 *   hand — that is, whether it is a label the model coined rather than one a person
 *   typed. Drawn as one small `AI` mark, never as a second hue and never as a
 *   suggestion: AI tags are applied, not proposed.
 *
 *   **Derived on every read, never stored.** Provenance is recorded per row, on
 *   `bookmark_tags.origin`, and this is an aggregate over those rows — so putting the
 *   tag on something by hand makes it yours on the next read, with nothing to migrate
 *   and no second source of truth that can disagree with the rows it came from. The
 *   query in `Bookmarks.sq` argues it at length.
 *
 *   **No default, unlike most optional-looking fields on this wire.** The usual rule
 *   here is additive-with-a-default, so that an older client can parse a newer server;
 *   it does not apply to a field that arrives in the same commit as the only route that
 *   ever sends it. A default would instead mean this server's JSON omits the field
 *   whenever it is false — `kotlinx.serialization` does not encode defaults — and the
 *   route's answer would not be the three-field shape it is documented as.
 */
@Serializable
data class TagUse(
    val tag: String,
    val uses: Long,
    val fromAi: Boolean,
)

/**
 * How a field that takes a `#tag` behaves — written **once**, here, on purpose.
 *
 * ── Why this is prose in a shared module rather than one implementation ───────
 *
 * There are two implementations of this and there have to be. The web control is
 * Kotlin/JS in `:web`; the bookmarklet popup's field is a hand-written script in
 * `Bookmarklet.kt`, and it cannot use the control — the popup is server-rendered HTML
 * with one small inline script *deliberately*, because booting the bundle to type two
 * tags means loading the toolkit, restoring the workspace and fetching every bookmark.
 * That file's header argues it in full.
 *
 * So the rules are stated once, in the one module both compile against, and both
 * implementations cite this object rather than each other. What that buys is the only
 * thing that matters here: the two cannot drift on the rules that decide whether a
 * collection stays tidy — **existing tags first, ordered by count, the count shown,
 * create last.**
 *
 * ── The rules ────────────────────────────────────────────────────────────────
 *
 * **The fragment is the token, not the field.** What the menu filters on is the `#…`
 * word under the caret, not the whole value. This is the rule that rules out the
 * obvious implementation — `<input list>` plus a `<datalist>`, which matches the input's
 * *entire* value and therefore stops matching the moment the field holds more than one
 * tag. A field reading `#rust #we` must offer `#web`.
 *
 * **A word without a `#` is never a tag.** It stays free text: in the list window's
 * search field it searches title, URL and summary, and nowhere does it become a label.
 *
 * **The menu is existing tags matching the fragment, ordered by [TagUse.uses] descending
 * and then by name, with the count shown on every row.** Ties broken by name so the
 * order is total and two reads of the same collection agree.
 *
 * **`Create "#…"` is always last, never first**, and only where creating a tag means
 * anything. It does in the edit window and in the popup, which file a bookmark. It does
 * not in the search field, which filters a collection it already holds — a tag nobody
 * has used matches nothing, so offering to create one there is offering to filter to
 * zero results.
 *
 * **A tag already committed in this field is not offered again**, in either row.
 *
 * **The first row is highlighted; arrow keys move the highlight.**
 *
 * **`⏎` commits** — the highlighted row's tag when the menu is open, the fragment as
 * typed when it is not — **and leaves the caret where the fragment began**, so a second
 * `#tag` can follow immediately without reaching for anything. The committed tag leaves
 * the text and becomes a chip; the text is what is still being typed.
 *
 * **`⌥⏎` excludes**, producing a struck-through chip, and it is the one key that differs
 * between the fields: only in the search field does a tag mean a filter, so only there
 * can it mean *not* this one. Where exclusion is not offered, `⌥⏎` commits plainly
 * rather than doing nothing — a modifier held by accident should not swallow the key.
 *
 * **`Esc` commits nothing.** It closes the menu and leaves the text exactly as typed, so
 * typing straight past the menu is always allowed. Nothing is committed until `⏎` — while
 * the typing is still going on, which is what the next rule qualifies.
 *
 * **Leaving a field where everything is a tag commits what is left in it.** `⏎` is the
 * rule *while typing*; moving away — Tab, a click elsewhere, pressing Save — is somebody
 * saying they are done, and a field that silently discarded the tag they had just typed
 * would be a worse bug than any of this prevents. So the leftover text is folded into
 * chips rather than dropped, **as typed and never as the highlighted row**: the highlight
 * is an offer the menu makes to `⏎`, and taking it on the way out would file a tag nobody
 * chose.
 *
 * This holds only where the field has no free text to be — the edit window, the settings
 * lists and the popup, where every word is a label. **The list window's search field is
 * the exception and must not do it**: half-typed text there is a search over title, URL
 * and summary, and committing it would turn a search somebody was still typing into a
 * filter. It is also the field that cannot create tags, so the tag it committed would
 * match nothing.
 *
 * The popup reaches this its own way, and correctly: it has no blur to hang it on because
 * it is a form, so it folds the leftover in on submit. Same rule, same moment — the person
 * said they were done.
 *
 * ── And what the host owns ───────────────────────────────────────────────────
 *
 * **The set of committed tags belongs to the host, not to the control.** The control is
 * told what is committed and reports what changed; it never holds the set privately.
 * That is what lets the list window's tag rail and its search field be two views of one
 * filter — removing a chip in the rail removes it in the field — without either knowing
 * about the other.
 */
object TagInputRules {
    /** The marker every tag carries, and the character that opens the menu. */
    const val MARKER: Char = '#'

    /**
     * How many existing tags the menu shows at once.
     *
     * A cap rather than a scroll: past a handful the list has stopped being something
     * read at a glance, and the answer to "my tag is not in it" is to type another
     * letter, which is faster than reaching for the mouse. `Create …` sits below
     * whatever survives this and is not counted by it.
     */
    const val MENU_LIMIT: Int = 8
}
